import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import { replace, join, map } from "lodash-es";

export default class extends Controller {
  static targets = [
    "element",
    "newElement",
    "sortableElement",
    "editFormButton",
    "newElementButton",
    "errorBox",
    "errorBoxHeader",
    "errorBoxList",
    "elementSelector",
  ];

  static outlets = ["audit--form-element"];

  static values = {
    openElements: { type: Array, default: [] },
    submissionStatuses: { type: Object, default: {} },
  };

  connect() {
    this.initializeSortable();
    this.isDraggableEnabled = true; // Initially disabled
    this.toggleDraggable();
  }

  disconnect() {
    this.sortable.destroy();
  }

  initializeSortable() {
    let el = document.getElementById("form-elements");
    this.sortable = Sortable.create(el, {
      onEnd: this.onEnd.bind(this),
    });
  }

  onEnd(e) {
    if (e.oldIndex !== e.newIndex) {
      const element = e.item;
      const form = element.querySelector(".audit-form-for-display-order");
      if (form) {
        const input = element.querySelector(
          ".audit-form-element-display-order"
        );
        input.value = e.newIndex + 1;
        form.requestSubmit();
      }
    }
  }

  toggleEdit() {
    this.element.classList.toggle("edit");
    const buttonText = this.element.classList.contains("edit")
      ? "Finish editing"
      : "Edit elements";
    this.editFormButtonTarget.textContent = buttonText;

    if (this.hasNewElementButtonTarget) {
      this.toggleNewElementButton();
    }

    this.toggleDraggable();
  }

  toggleNewElementButton() {
    this.newElementButtonTarget.classList.toggle("hide");
  }

  toggleDraggable() {
    this.isDraggableEnabled = !this.isDraggableEnabled;
    this.sortable.option("disabled", !this.isDraggableEnabled);
  }

  scrollToNewElement() {
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 100);
  }

  removeNewElement(e) {
    this.handleRemoveElement(e);
    e.currentTarget.parentElement.parentElement.remove();
  }

  removeElementSelector() {
    if (this.hasElementSelectorTarget) {
      this.elementSelectorTarget.parentElement.parentElement.remove();
    }
  }

  removeIdFromSubmissionValues(id) {
    delete this.submissionStatusesValue[id];
  }

  removeIdFromArray(id) {
    this.openElementsValue = this.openElementsValue.filter((e) => e !== id);
  }

  handleRemoveElement(e) {
    const id = e.currentTarget.parentElement.parentElement.id;
    this.removeIdFromSubmissionValues(id);
    this.removeIdFromArray(id);
    this.populateErrorBox();
  }

  // Error Handling

  checkForEditingElements() {
    this.removeElementSelector();
    if (this.openElementsValue.length === 0) {
      this.toggleEdit();
    } else {
      this.openElementsValue.forEach((element) => {
        const frame = document.getElementById(element);
        const form =
          frame.querySelector("#edit_audit_form_element") ||
          frame.querySelector("#new_audit_form_element");
        if (form) {
          this.submissionStatusesValue[element] = "pending";
          form.requestSubmit();
        }
      });

      // Wait for all submissions to complete
      this.waitForSubmissions().then(() => {
        const allSuccessful = Object.values(this.submissionStatusesValue).every(
          (status) => status === "success"
        );
        if (allSuccessful) {
          this.toggleEdit();
        }
      });
    }
  }

  populateErrorBox() {
    const allSuccessful = Object.values(this.submissionStatusesValue).every(
      (status) => status === "success"
    );
    if (allSuccessful) {
      for (const key in this.submissionStatusesValue) {
        delete this.submissionStatusesValue[key];
      }
      this.hideErrorBox();
    } else {
      let listMessage = "";
      let errorElem;
      let errorKeys = [];
      for (const [key, value] of Object.entries(this.submissionStatusesValue)) {
        if (value === "error") {
          errorKeys.push(key);
        } else {
          delete this.submissionStatusesValue[key];
        }
      }

      const errorSelector = join(
        map(errorKeys, (k) => {
          if (k == "new-form-element") {
            return '[data-id="element-"]';
          } else {
            return `[data-id="${replace(k, "form-", "")}"]`;
          }
        })
      );
      this.element
        .querySelectorAll("#form-elements > turbo-frame")
        .forEach((frame, targetElementIndex) => {
          errorElem = frame.querySelector(errorSelector);
          if (errorElem) {
            listMessage += `<li>Error in element #${
              targetElementIndex + 1
            }: <a href="#${
              errorElem.parentElement.id
            }" onclick="document.getElementById('${
              errorElem.parentElement.id
            }').scrollIntoView()">Show</a></li>`;
          }
        });

      const pluralize = errorKeys.length > 1 ? "elements" : "element";
      const headerMessage = `${errorKeys.length} ${pluralize} failed to submit:`;

      this.errorBoxHeaderTarget.innerHTML = headerMessage;
      this.errorBoxListTarget.innerHTML = listMessage;
      this.errorBoxTarget.classList.remove("hide");
    }
  }

  hideErrorBox() {
    this.errorBoxHeaderTarget.innerHTML = "";
    this.errorBoxListTarget.innerHTML = "";
    this.errorBoxTarget.classList.add("hide");
  }

  waitForSubmissions() {
    return new Promise((resolve) => {
      const checkStatus = () => {
        const allDone = this.openElementsValue.every((element) =>
          ["success", "error"].includes(this.submissionStatusesValue[element])
        );
        if (allDone) {
          resolve();
        } else {
          setTimeout(checkStatus, 100); // Check again in 100ms
        }
      };
      checkStatus();
    });
  }
}
